import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, FileText, Search, Eye, Trash2, Download, Mail } from "lucide-react";

const InvoicesPage = () => {
  const { currentOrg } = useOrg();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchInvoices = async () => {
    if (!currentOrg) return;
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      
      const response = await axios.get(`${API}/invoices`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id },
        params
      });
      setInvoices(response.data);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [currentOrg, statusFilter]);

  const handleDelete = async (invoiceId) => {
    if (!confirm("Are you sure you want to delete this invoice?")) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Invoice deleted");
      await fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.customer?.business_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700';
      case 'sent': return 'bg-blue-50 text-blue-700';
      case 'draft': return 'bg-zinc-100 text-zinc-700';
      case 'partially_paid': return 'bg-amber-50 text-amber-700';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Invoices</h1>
          <p className="text-muted-foreground text-sm">Create and manage your invoices</p>
        </div>
        <Link to="/dashboard/invoices/new">
          <Button className="gap-2" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="invoice-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="partially_paid">Partial</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No invoices found</p>
            <Link to="/dashboard/invoices/new">
              <Button variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Create your first invoice
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Invoice</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Customer</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Due Date</th>
                  <th className="text-center py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Total</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Balance</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4">
                      <Link 
                        to={`/dashboard/invoices/${invoice.invoice_id}`} 
                        className="text-primary hover:underline font-medium"
                        data-testid={`invoice-link-${invoice.invoice_id}`}
                      >
                        {invoice.invoice_number}
                      </Link>
                    </td>
                    <td className="py-3 px-4">{invoice.customer?.business_name || '-'}</td>
                    <td className="py-3 px-4 text-muted-foreground">{invoice.invoice_date ? new Date(invoice.invoice_date + 'T00:00:00').toLocaleDateString('en-US') : ''}</td>
                    <td className="py-3 px-4 text-muted-foreground">{invoice.due_date ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString('en-US') : ''}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {invoice.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">${invoice.grand_total?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={invoice.balance_due > 0 ? 'text-amber-600 font-medium' : 'text-muted-foreground'}>
                        ${invoice.balance_due?.toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Link to={`/dashboard/invoices/${invoice.invoice_id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadPdf(invoice.invoice_id, invoice.invoice_number)}
                        data-testid={`download-pdf-${invoice.invoice_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {invoice.status !== 'paid' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(invoice.invoice_id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
